import {
  ButtonProps,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from '@chakra-ui/react';
import { ReactNode, useCallback, useMemo, useState } from 'react';

import { runConfirmationOption } from './multi-option-confirmation-modal-utils';

export interface MultiOptionConfirmModalOption {
  key: string;
  label: string;
  description?: ReactNode;
  colorScheme?: string;
  variant?: ButtonProps['variant'];
  isDisabled?: boolean;
  onSelect: () => unknown;
}

interface MultiOptionConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message?: ReactNode;
  options: MultiOptionConfirmModalOption[];
  cancelLabel?: string;
}

export function MultiOptionConfirmModal({
  isOpen,
  onClose,
  title,
  message,
  options,
  cancelLabel = 'Cancel',
}: MultiOptionConfirmModalProps) {
  const [pendingKey, setPendingKey] = useState<string | null>(null);

  const optionByKey = useMemo(() => new Map(options.map(option => [option.key, option])), [options]);

  const handleSelect = useCallback(
    async (key: string) => {
      const option = optionByKey.get(key);
      if (!option || option.isDisabled) return;
      setPendingKey(key);
      try {
        await runConfirmationOption(option.onSelect, onClose);
      } catch (error) {
        console.error('Failed to run confirmation option', error);
      } finally {
        setPendingKey(null);
      }
    },
    [onClose, optionByKey],
  );

  const handleModalClose = useCallback(() => {
    if (pendingKey) {
      return;
    }
    onClose();
  }, [onClose, pendingKey]);

  const hasSingleOption = options.length === 1;
  const singleOption = hasSingleOption ? options[0] : null;
  const singleOptionDescription = hasSingleOption ? singleOption?.description : undefined;
  const shouldRenderOptionButtons = !hasSingleOption;

  return (
    <Modal isOpen={isOpen} onClose={handleModalClose} isCentered closeOnOverlayClick={!pendingKey}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton isDisabled={pendingKey !== null} />
        <ModalBody>
          {message ? (
            typeof message === 'string' ? (
              <Text mb={4}>{message}</Text>
            ) : (
              <div style={{ marginBottom: '16px' }}>{message}</div>
            )
          ) : null}
          {singleOptionDescription ? (
            <Stack spacing={1} mb={4}>
              <Text fontWeight="semibold">{singleOption?.label}</Text>
              <Text fontSize="sm" color="gray.600">
                {singleOptionDescription}
              </Text>
            </Stack>
          ) : null}
          {shouldRenderOptionButtons && (
            <Stack spacing={4}>
              {options.map(option => (
                <Button
                  key={option.key}
                  onClick={() => {
                    void handleSelect(option.key);
                  }}
                  colorScheme={option.colorScheme}
                  variant={option.variant ?? 'outline'}
                  isLoading={pendingKey === option.key}
                  isDisabled={option.isDisabled || (pendingKey !== null && pendingKey !== option.key)}
                  justifyContent="flex-start"
                  height="auto"
                  whiteSpace="normal"
                  textAlign="left"
                  textTransform="none"
                  paddingInline={4}
                  paddingBlock={3}
                  borderWidth={1}
                  borderColor={option.colorScheme ? `${option.colorScheme}.200` : 'gray.200'}
                  backgroundColor="white"
                  _hover={{
                    backgroundColor: option.colorScheme ? `${option.colorScheme}.50` : 'gray.50',
                  }}
                  _active={{
                    backgroundColor: option.colorScheme ? `${option.colorScheme}.100` : 'gray.100',
                  }}
                >
                  <Stack spacing={1} align="flex-start">
                    <Text fontWeight="semibold">{option.label}</Text>
                    {option.description ? (
                      <Text fontSize="sm" color="gray.600">
                        {option.description}
                      </Text>
                    ) : null}
                  </Stack>
                </Button>
              ))}
            </Stack>
          )}
        </ModalBody>
        <ModalFooter>
          {hasSingleOption ? (
            <Stack direction="row" spacing={3} justify="flex-end">
              <Button onClick={onClose} isDisabled={pendingKey !== null}>
                {cancelLabel}
              </Button>
              <Button
                onClick={() => {
                  if (!singleOption) return;
                  void handleSelect(singleOption.key);
                }}
                colorScheme={singleOption?.colorScheme}
                variant={singleOption?.variant ?? 'solid'}
                isLoading={pendingKey === singleOption?.key}
                isDisabled={singleOption?.isDisabled || (pendingKey !== null && pendingKey !== singleOption?.key)}
              >
                {singleOption?.label}
              </Button>
            </Stack>
          ) : (
            <Button onClick={onClose} isDisabled={pendingKey !== null}>
              {cancelLabel}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
